import { create } from "zustand";

export interface ToastAction {
  label: string;
  /** An address inside the app. */
  to: string;
}

export interface ToastInput {
  text: string;
  /** warning draws the eye (it is read out at once); info waits its turn. */
  tone?: "info" | "warning";
  action?: ToastAction;
  /**
   * How long it stays, in milliseconds. 0 keeps it until it is dismissed. A warning stays until it is dismissed: it often asks
   * for something (a colleague asks for a lock), and a person who looked away must still find it (UI-27).
   */
  timeoutMs?: number;
}

export interface ToastItem extends Required<Pick<ToastInput, "text" | "tone" | "timeoutMs">> {
  id: number;
  action?: ToastAction;
}

interface ToastState {
  toasts: ToastItem[];
  push(input: ToastInput): number;
  dismiss(id: number): void;
  clear(): void;
}

let nextId = 1;
const MAX_VISIBLE = 4;

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push(input) {
    const id = nextId++;
    const item: ToastItem = {
      id,
      text: input.text,
      tone: input.tone ?? "info",
      timeoutMs: input.timeoutMs ?? (input.tone === "warning" ? 0 : input.action ? 12_000 : 8_000),
      action: input.action,
    };
    set((state) => ({ toasts: [...state.toasts, item].slice(-MAX_VISIBLE) }));
    return id;
  },
  dismiss(id) {
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
  },
  clear() {
    set({ toasts: [] });
  },
}));

/** Show a short message. Usable from anywhere, inside or outside a component. */
export function showToast(input: ToastInput): number {
  return useToastStore.getState().push(input);
}
